import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { SocketConnectionService } from './socket-connection.service';

@Injectable({
  providedIn: 'root'
})
export class SocketDataService {

  // Stream for chart data (historical + live)
  private chartDataSubject = new Subject<any>();
  public chartData$: Observable<any> =
    this.chartDataSubject.asObservable();

  // Stream for chart updates (real-time updates from backend cron)
  private chartUpdateSubject = new Subject<any>();
  public chartUpdate$: Observable<any> =
    this.chartUpdateSubject.asObservable();

  // Stream for chart drawings (variable name "drawings" or chartDrawings from socket)
  private chartDrawingsSubject = new Subject<{ chartDrawings: any[]; symbol?: string; timeframe?: string; interval?: string }>();
  public chartDrawings$: Observable<{ chartDrawings: any[]; symbol?: string; timeframe?: string; interval?: string }> =
    this.chartDrawingsSubject.asObservable();

  // Stream for socket errors
  public errors$: Observable<string | null>;

  private listenersRegistered = false;

  /** Suppress duplicate `get:chart:data` emits (same payload within a short window). */
  private lastChartDataEmitKey = '';
  private lastChartDataEmitAt = 0;
  /** Wider window avoids duplicate `get:chart:data` when route + UI fire nearly the same request back-to-back. */
  private static readonly CHART_DATA_EMIT_DEDUPE_MS = 1200;

  constructor(
    private socketConnection: SocketConnectionService
  ) {
    this.errors$ = this.socketConnection.errors$;
  }


  /** Call this AFTER connect() */
  init(): void {
    if (this.listenersRegistered) return;

    // Listen for initial chart data
    this.socketConnection.on('chart:data', (data) => {
      console.log('[SocketDataService] 📊 chart:data', data);
      this.chartDataSubject.next(data);

      // Extract and emit drawings from chart:data (backend may send data + drawings together)
      const chartDrawings = data?.drawings ?? data?.chartDrawings ?? data?.data?.chartDrawings ??
        data?.data?.drawings ?? [];
      if (Array.isArray(chartDrawings) && chartDrawings.length > 0) {
        this.chartDrawingsSubject.next({
          chartDrawings,
          symbol: data?.symbol ?? data?.data?.symbol,
          timeframe: data?.timeframe ?? data?.timeRange ?? data?.range ?? data?.data?.timeframe,
          interval: data?.interval ?? data?.data?.interval
        });
      }
    });

    // Listen for real-time chart updates from backend cron
    this.socketConnection.on('chart:update', (data) => {
      console.log('[SocketDataService] 🔄 chart:update', data);
      this.chartUpdateSubject.next(data);
    });

    // Listen for chart drawings – payload may have drawings, chartDrawings (symbol, interval, range/timeframe)
    this.socketConnection.on('chart:drawings', (data: any) => {
      console.log('[SocketDataService] 📐 chart:drawings', data);
      const chartDrawings = data?.drawings ?? data?.chartDrawings ?? data?.data?.chartDrawings ??
        data?.data?.drawings ?? (Array.isArray(data) ? data : []);
      if (Array.isArray(chartDrawings) && chartDrawings.length > 0) {
        this.chartDrawingsSubject.next({
          chartDrawings,
          symbol: data?.symbol ?? data?.data?.symbol,
          timeframe: data?.timeframe ?? data?.timeRange ?? data?.range ?? data?.data?.timeframe,
          interval: data?.interval ?? data?.data?.interval
        });
      }
    });

    this.listenersRegistered = true;
  }

  // =====================================================
  // EMIT EVENTS
  // =====================================================

  /**
   * Request historical chart data
   */
  getChartData(
    symbol: string,
    interval: string,
    range: string,
    to?: number,
    limit?: number,
    force = false
  ): void {
    const payload: any = {
      symbol,
      interval,
      range
    };

    if (typeof to === 'number' && Number.isFinite(to)) {
      payload.to = Math.floor(to);
    }
    if (typeof limit === 'number' && Number.isFinite(limit) && limit > 0) {
      payload.limit = Math.floor(limit);
    }

    const key = JSON.stringify({
      s: (symbol || '').toUpperCase(),
      i: interval,
      r: range,
      to: payload.to ?? null,
      l: payload.limit ?? null
    });
    const now = Date.now();
    if (
      !force &&
      key === this.lastChartDataEmitKey &&
      now - this.lastChartDataEmitAt < SocketDataService.CHART_DATA_EMIT_DEDUPE_MS
    ) {
      console.warn('[SocketDataService] Skipped duplicate get:chart:data within dedupe window', payload);
      return;
    }
    this.lastChartDataEmitKey = key;
    this.lastChartDataEmitAt = now;

    this.socketConnection.emit('get:chart:data', payload);
  }

  /**
   * Request chart drawings from socket (backend can respond with chart:drawings)
   */
  getChartDrawings(symbol: string, interval: string, range: string): void {
    this.socketConnection.emit('get:chart:drawings', {
      symbol,
      interval,
      range
    });
  }

  /**
   * Subscribe to live chart updates
   */
  subscribeChart(
    symbol: string,
    interval: string
  ): void {
    this.socketConnection.emit('subscribe:chart', {
      symbol,
      interval
    });
  }

  /**
   * Unsubscribe from live chart updates
   */
  unsubscribeChart(
    symbol: string,
    interval: string
  ): void {
    this.socketConnection.emit('unsubscribe:chart', {
      symbol,
      interval
    });
  }

  // =====================================================
  // CLEANUP
  // =====================================================
  destroy(): void {
    this.socketConnection.off('chart:data');
    this.socketConnection.off('chart:update');
    this.socketConnection.off('chart:drawings');
    this.chartDataSubject.complete();
    this.chartUpdateSubject.complete();
    this.chartDrawingsSubject.complete();
  }
}
